import { paramCase } from 'src/utils/change-case';

import { _id, _postTitles } from 'src/_mock/assets';

// ----------------------------------------------------------------------

const MOCK_ID = _id[1];

const MOCK_TITLE = _postTitles[2];

export const ROOTS = {
  AUTH: '/auth',
  DASHBOARD: '/dashboard',
};

// ----------------------------------------------------------------------

export const paths = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  faqs: '/faqs',
  page403: '/error/403',
  page404: '/error/404',
  page500: '/error/500',
  components: '/components',
  docs: 'https://docs.minimals.cc',
  changelog: 'https://docs.minimals.cc/changelog',
  zoneStore: 'https://mui.com/store/items/zone-landing-page/',
  minimalStore: 'https://mui.com/store/items/minimal-dashboard/',
  freeUI: 'https://mui.com/store/items/minimal-dashboard-free/',
  figma: 'https://www.figma.com/design/cAPz4pYPtQEXivqe11EcDE/%5BPreview%5D-Minimal-Web.v6.0.0',
  product: {
    root: `/product`,
    checkout: `/product/checkout`,
    details: (id) => `/product/${id}`,
    demo: { details: `/product/${MOCK_ID}` },
  },
  post: {
    root: `/post`,
    details: (title) => `/post/${paramCase(title)}`,
    demo: { details: `/post/${paramCase(MOCK_TITLE)}` },
  },
  // AUTH
  auth: {
    amplify: {
      signIn: `${ROOTS.AUTH}/amplify/sign-in`,
      verify: `${ROOTS.AUTH}/amplify/verify`,
      signUp: `${ROOTS.AUTH}/amplify/sign-up`,
      updatePassword: `${ROOTS.AUTH}/amplify/update-password`,
      resetPassword: `${ROOTS.AUTH}/amplify/reset-password`,
    },
    jwt: {
      signIn: `${ROOTS.AUTH}/jwt/sign-in`,
      signUp: `${ROOTS.AUTH}/jwt/sign-up`,
    },
    firebase: {
      signIn: `${ROOTS.AUTH}/firebase/sign-in`,
      verify: `${ROOTS.AUTH}/firebase/verify`,
      signUp: `${ROOTS.AUTH}/firebase/sign-up`,
      resetPassword: `${ROOTS.AUTH}/firebase/reset-password`,
    },
    auth0: {
      signIn: `${ROOTS.AUTH}/auth0/sign-in`,
    },
    supabase: {
      signIn: `${ROOTS.AUTH}/supabase/sign-in`,
      verify: `${ROOTS.AUTH}/supabase/verify`,
      signUp: `${ROOTS.AUTH}/supabase/sign-up`,
      updatePassword: `${ROOTS.AUTH}/supabase/update-password`,
      resetPassword: `${ROOTS.AUTH}/supabase/reset-password`,
    },
  },
  authDemo: {
    split: {
      signIn: `${ROOTS.AUTH_DEMO}/split/sign-in`,
      signUp: `${ROOTS.AUTH_DEMO}/split/sign-up`,
      resetPassword: `${ROOTS.AUTH_DEMO}/split/reset-password`,
      updatePassword: `${ROOTS.AUTH_DEMO}/split/update-password`,
      verify: `${ROOTS.AUTH_DEMO}/split/verify`,
    },
    centered: {
      signIn: `${ROOTS.AUTH_DEMO}/centered/sign-in`,
      signUp: `${ROOTS.AUTH_DEMO}/centered/sign-up`,
      resetPassword: `${ROOTS.AUTH_DEMO}/centered/reset-password`,
      updatePassword: `${ROOTS.AUTH_DEMO}/centered/update-password`,
      verify: `${ROOTS.AUTH_DEMO}/centered/verify`,
    },
  },
  // DASHBOARD
  dashboard: {
    root: ROOTS.DASHBOARD,
    general: {
      app: `${ROOTS.DASHBOARD}/app`,
      ecommerce: `${ROOTS.DASHBOARD}/ecommerce`,
      analytics: `${ROOTS.DASHBOARD}/analytics`,
      banking: `${ROOTS.DASHBOARD}/banking`,
      booking: `${ROOTS.DASHBOARD}/booking`,
      file: `${ROOTS.DASHBOARD}/file`,
      course: `${ROOTS.DASHBOARD}/course`,
    },
    user: {
      root: `${ROOTS.DASHBOARD}/user`,
      new: `${ROOTS.DASHBOARD}/user/new`,
      list: `${ROOTS.DASHBOARD}/user/list`,
      cards: `${ROOTS.DASHBOARD}/user/cards`,
      profile: `${ROOTS.DASHBOARD}/user/profile`,
      account: `${ROOTS.DASHBOARD}/user/account`,
      roles: `${ROOTS.DASHBOARD}/user/roles`,
      edit: (id) => `${ROOTS.DASHBOARD}/user/${id}/edit`,
      demo: {
        edit: `${ROOTS.DASHBOARD}/user/${MOCK_ID}/edit`,
      },
    },
    games: {
      root: `${ROOTS.DASHBOARD}/games`,
      list: `${ROOTS.DASHBOARD}/games/list`,
      new: `${ROOTS.DASHBOARD}/games/new`,
      details: (id) => `${ROOTS.DASHBOARD}/games/${id}`,
    },
    gameConfiguration: {
      root: `${ROOTS.DASHBOARD}/games-configuration`,
      list: `${ROOTS.DASHBOARD}/games-configuration/list`,
      external: `${ROOTS.DASHBOARD}/games-configuration/external`,
      new: `${ROOTS.DASHBOARD}/games-configuration/new`,
      details: (id) => `${ROOTS.DASHBOARD}/games-configuration/${id}`,
    },
    featureConfiguration: {
      root: `${ROOTS.DASHBOARD}/feature-configuration`,
      bonusBuy: `${ROOTS.DASHBOARD}/feature-configuration/bonus-buy`,
      anteBet: `${ROOTS.DASHBOARD}/feature-configuration/ante-bet`,
      new: `${ROOTS.DASHBOARD}/feature-configuration/new`,
      details: (id) => `${ROOTS.DASHBOARD}/feature-configuration/${id}`,
    },
    engineManagement: {
      root: `${ROOTS.DASHBOARD}/engine-management`,
      list: `${ROOTS.DASHBOARD}/engine-management/list`,
    },
    clientConfiguration: {
      root: `${ROOTS.DASHBOARD}/client-configuration`,
      list: `${ROOTS.DASHBOARD}/client-configuration/list`,
      loader: `${ROOTS.DASHBOARD}/client-configuration/loader`,
      new: `${ROOTS.DASHBOARD}/client-configuration/new`,
      details: (id) => `${ROOTS.DASHBOARD}/client-configuration/${id}`,
    },
    selectPageConfiguration: {
      root: `${ROOTS.DASHBOARD}/select-page-configuration`,
    },
    jackpotConfiguration: {
      root: `${ROOTS.DASHBOARD}/jackpot-configuration`,
      list: `${ROOTS.DASHBOARD}/jackpot-configuration/list`,
      details: (id) => `${ROOTS.DASHBOARD}/jackpot-configuration/${id}`,

      liquidityPools: {
        root: `${ROOTS.DASHBOARD}/jackpot-configuration/liquidity-pools`,
        list: `${ROOTS.DASHBOARD}/jackpot-configuration/liquidity-pools/list`,
      },
      groups: {
        root: `${ROOTS.DASHBOARD}/jackpot-configuration/groups`,
        list: `${ROOTS.DASHBOARD}/jackpot-configuration/groups/list`,
        details: (id) => `${ROOTS.DASHBOARD}/jackpot-configuration/groups/${id}`,
      },
    },
    investigation: {
      root: `${ROOTS.DASHBOARD}/investigation`,
      list: `${ROOTS.DASHBOARD}/investigation/list`,
    },
    incidents: {
      root: `${ROOTS.DASHBOARD}/incidents`,
      list: `${ROOTS.DASHBOARD}/incidents/list`,
    },
    testing: {
      root: `${ROOTS.DASHBOARD}/testing`,

      rtpVerification: {
        root: `${ROOTS.DASHBOARD}/testing/rtp-verification`,
        list: `${ROOTS.DASHBOARD}/testing/rtp-verification/list`,
        details: (id) => `${ROOTS.DASHBOARD}/testing/rtp-verification/${id}`,
      },
      formBuilder: {
        root: `${ROOTS.DASHBOARD}/testing/form-builder`,
        list: `${ROOTS.DASHBOARD}/testing/form-builder/list`,
      },
      load: {
        root: `${ROOTS.DASHBOARD}/testing/load`,
        list: `${ROOTS.DASHBOARD}/testing/load/list`,
        details: (id) => `${ROOTS.DASHBOARD}/testing/load/${id}`,
      },
      vulnerability: {
        root: `${ROOTS.DASHBOARD}/testing/vulnerability`,
        list: `${ROOTS.DASHBOARD}/testing/vulnerability/list`,
        details: (id) => `${ROOTS.DASHBOARD}/testing/vulnerability/${id}`,
        cases: `${ROOTS.DASHBOARD}/testing/vulnerability/cases`,
      },
      gameSimulation: {
        root: `${ROOTS.DASHBOARD}/testing/game-simulation`,
        list: `${ROOTS.DASHBOARD}/testing/game-simulation/list`,
      },
      thresholds: {
        root: `${ROOTS.DASHBOARD}/testing/thresholds`,
      },
      callbackTool: {
        root: `${ROOTS.DASHBOARD}/testing/callback-tool`,
      },
      analytic: {
        root: `${ROOTS.DASHBOARD}/testing/analytic`,
        list: `${ROOTS.DASHBOARD}/testing/analytic/list`,
      },
    },
    operators: {
      root: `${ROOTS.DASHBOARD}/operators`,
      list: `${ROOTS.DASHBOARD}/operators/list`,
      new: `${ROOTS.DASHBOARD}/operators/new`,
      details: (id) => `${ROOTS.DASHBOARD}/operators/${id}`,
    },
    bonuses: {
      root: `${ROOTS.DASHBOARD}/bonuses`,
      list: `${ROOTS.DASHBOARD}/bonuses/list`,
      userBonuses: `${ROOTS.DASHBOARD}/bonuses/user`,
    },
    dictionaries: {
      root: `${ROOTS.DASHBOARD}/dictionaries`,
    },
    clientEvents: {
      root: `${ROOTS.DASHBOARD}/client-events`,
    },
    audit: {
      root: `${ROOTS.DASHBOARD}/audit`,
      list: `${ROOTS.DASHBOARD}/audit/list`,
    },
  },
};
